import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { DotsVerticalIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import './Global.css';

const PositionsManagement = () => {
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);
  const [deletingPosition, setDeletingPosition] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // all | militar | civil
  const [formData, setFormData] = useState({
    name: '',
    personnelType: 'militar',
    exemptFromAutoClose: false,
  });

  const pagination = usePagination(filteredPositions, 10);
  const { setPage } = pagination;

  useEffect(() => {
    loadPositions();
  }, []);

  // Cadastro anterior à separação por tipo vem sem personnelType: é militar.
  const typeOf = (position) => position.personnelType || 'militar';

  useEffect(() => {
    const filtered = positions.filter(
      (position) =>
        position.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (typeFilter === 'all' || typeOf(position) === typeFilter),
    );
    setFilteredPositions(filtered);
    setPage(1);
  }, [searchTerm, positions, typeFilter, setPage]);

  const loadPositions = async () => {
    try {
      const response = await api.get('/positions');
      setPositions(response.data);
      setFilteredPositions(response.data);
    } catch (error) {
      console.error('Erro ao carregar funções:', error);
      toast.error('Erro ao carregar funções');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPosition) {
        await api.patch(`/positions/${editingPosition.id}`, formData);
        toast.success('Função atualizada com sucesso!');
      } else {
        await api.post('/positions', formData);
        toast.success('Função criada com sucesso!');
      }
      handleCloseModal();
      loadPositions();
    } catch (error) {
      console.error('Erro ao salvar função:', error);
      toast.error('Erro ao salvar função: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (position) => {
    setEditingPosition(position);
    setFormData({
      name: position.name,
      personnelType: position.personnelType || 'militar',
      exemptFromAutoClose: position.exemptFromAutoClose || false,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPosition(null);
    setFormData({
      name: '',
      personnelType: typeFilter === 'civil' ? 'civil' : 'militar',
      exemptFromAutoClose: false,
    });
  };

  const openDeleteModal = (position) => {
    setPositionToDelete(position);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingPosition) return;
    setShowDeleteModal(false);
    setPositionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!positionToDelete) return;

    setDeletingPosition(true);
    try {
      await api.delete(`/positions/${positionToDelete.id}`);
      toast.success('Função excluída com sucesso!');
      loadPositions();
    } catch (error) {
      console.error('Erro ao excluir função:', error);
      toast.error('Erro ao excluir função: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeletingPosition(false);
      setShowDeleteModal(false);
      setPositionToDelete(null);
    }
  };

  const currentPositions = pagination.pageItems;

  if (loading) return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando funções...</p>
      </div>
    </>
  );

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1 style={{ margin: 0 }}>Gerenciamento de Funções</h1>
            <p className="subtitle">{filteredPositions.length} {filteredPositions.length === 1 ? 'função' : 'funções'} cadastrada{filteredPositions.length === 1 ? '' : 's'}</p>
          </div>
          <div className="header-actions">
            <button type="button" className="btn-primary" onClick={() => setShowModal(true)}>
              + Nova Função
            </button>
          </div>
        </div>

        <div className="table-controls">
          <div className="seg" role="tablist" aria-label="Filtrar funções por tipo">
            {[
              { v: 'all', label: 'Todas' },
              { v: 'militar', label: 'Militares' },
              { v: 'civil', label: 'Civis' },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                role="tab"
                aria-selected={typeFilter === opt.v}
                className={`seg-btn seg-btn--${opt.v} ${typeFilter === opt.v ? 'active' : ''}`}
                onClick={() => setTypeFilter(opt.v)}
              >
                {opt.v !== 'all' && <i className={`type-dot type-dot--${opt.v}`} />}
                {opt.label}
                <span className="seg-btn__count">
                  {opt.v === 'all'
                    ? positions.length
                    : positions.filter((p) => typeOf(p) === opt.v).length}
                </span>
              </button>
            ))}
          </div>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Buscar função..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Nome da Função</th>
                <th>Destinada a</th>
                <th>Data de Criação</th>
                <th className="text-center">Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {currentPositions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center empty-state">
                    <strong>
                      {searchTerm ? 'Nenhuma função encontrada' : 'Nenhuma função cadastrada'}
                    </strong>
                    {searchTerm
                      ? 'Tente outro termo de busca ou mude o filtro de tipo.'
                      : 'Clique em "Nova Função" para cadastrar a primeira.'}
                  </td>
                </tr>
              ) : (
                currentPositions.map((position) => (
                  <tr key={position.id}>
                    <td className="cell-primary">{position.name}</td>
                    <td>
                      <span
                        className={`badge ${typeOf(position) === 'civil' ? 'badge-civil' : 'badge-militar'}`}
                      >
                        {typeOf(position) === 'civil' ? 'Civil' : 'Militar'}
                      </span>
                    </td>
                    <td>{new Date(position.createdAt).toLocaleDateString('pt-BR')}</td>
                    <td className="text-center">
                      <span className={`badge ${position.isActive ? 'badge-success' : 'badge-danger'}`}>
                        {position.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="text-center">
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button className="icon-button" aria-label="Ações">
                            <DotsVerticalIcon />
                          </button>
                        </DropdownMenu.Trigger>

                        <DropdownMenu.Portal>
                          <DropdownMenu.Content className="dropdown-content" sideOffset={5}>
                            <DropdownMenu.Item
                              className="dropdown-item"
                              onSelect={() => handleEdit(position)}
                            >
                              <Pencil1Icon />
                              <span>Editar</span>
                            </DropdownMenu.Item>

                            <DropdownMenu.Separator className="dropdown-separator" />

                            <DropdownMenu.Item
                              className="dropdown-item dropdown-item-danger"
                              onSelect={() => openDeleteModal(position)}
                            >
                              <TrashIcon />
                              <span>Excluir</span>
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          id="positions"
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          from={pagination.from}
          to={pagination.to}
          pageSize={pagination.pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
          itemLabel="funções"
        />

        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingPosition ? 'Editar Função' : 'Nova Função'}</h2>
                <button className="modal-close" onClick={handleCloseModal}>
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="form">
                <div className="form-group">
                  <label>Nome da Função *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder={
                      formData.personnelType === 'civil'
                        ? 'Ex: Serviços Gerais'
                        : 'Ex: Instrutor de Matemática'
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Destinada a *</label>
                  <div className="seg seg--block">
                    {[
                      { v: 'militar', label: 'Efetivo militar' },
                      { v: 'civil', label: 'Pessoal civil' },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        className={`seg-btn seg-btn--${opt.v} ${formData.personnelType === opt.v ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, personnelType: opt.v })}
                      >
                        <i className={`type-dot type-dot--${opt.v}`} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <small className="form-hint">
                    A função só aparece no cadastro do tipo escolhido. Civil tem funções
                    próprias — Serviços Gerais, portaria, apoio.
                  </small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.exemptFromAutoClose}
                      onChange={(e) => setFormData({ ...formData, exemptFromAutoClose: e.target.checked })}
                    />
                    <span>Isento de Auto-Close (horário livre)</span>
                  </label>
                  <small className="form-hint">
                    Usuários com esta função podem registrar entrada/saída a qualquer horário. Horas calculadas exatamente (sem arredondamento ou truncamento).
                  </small>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={handleCloseModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingPosition ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeleteModal && positionToDelete && (
          <div className="modal-overlay" onClick={closeDeleteModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Excluir função</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={closeDeleteModal}
                  disabled={deletingPosition}
                >
                  ×
                </button>
              </div>

              <div className="confirmation-body">
                <p>
                  Tem certeza que deseja excluir a função{' '}
                  <strong>{positionToDelete.name}</strong>?
                </p>
                <p className="warning-text">
                  Essa ação é permanente. Militares associados a esta função não serão afetados.
                </p>
              </div>

              <div className="modal-actions" style={{ padding: '0 24px 24px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={deletingPosition}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deletingPosition}
                >
                  {deletingPosition ? 'Removendo...' : 'Sim, excluir'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PositionsManagement;
